import { Chart } from 'highcharts';
import { Ref, RefObject } from 'react';

export type ISettingItem = {
  id: number;
  value: ICurrency | IUnit | IDecimal;
  default: boolean;
};

export interface IPeriod {
  startPeriod: number;
  startYear: number;
  endPeriod: number;
  endYear: number;
}

export type IFilterResponse = {
  [key: string]: Array<{
    value: string;
  }>;
};

export interface IOption {
  label: string;
  id: string;
  isSelected?: boolean;
  options?: IOption[];
}

export interface IFilter {
  id: string;
  name: string;
  options: IOption[];
}

export interface ICurrencyFormat {
  currency: ICurrency;
  decimal: IDecimal;
  unit: IUnit;
}

export type IQueryFilter = { [x: string]: string };

export interface ISettingsResponse {
  availableRange: IPeriod;
  currencies: ISettingItem[];
  units: ISettingItem[];
  decimals: ISettingItem[];
  selectedYear: IPeriod;
  clientName: string;
}

export interface ISettings {
  currencyId?: number;
  unitId?: number;
  decimalId?: number;
  calendarYear?: IPeriod;
  clientName: string;
}

export interface ISpendContribution {
  contribution: string;
  cost: number;
  dimValueL1: string;
  dimValueL2: string;
  numberOfEmployees?: number;
  permanentOrContract?: string;
}

export interface ISpendContributionResponse {
  spendItems: ISpendContribution[];
}

export interface IHeadlinesResponsePeriod {
  endDate: string;
  numberOfEmployees?: number;
  numberOfUniqueGeographies?: number;
  numberOfUniqueVendors?: number;
  revenue: number;
  startDate: string;
  totalCost?: number;
  totalCostAsPercentageOfRevenue?: number;
}

export interface IHeadlinesResponse {
  comparisonPeriod: IHeadlinesResponsePeriod;
  currentPeriod: IHeadlinesResponsePeriod;
  numberOfEmployeesVariance?: number;
  numberOfUniqueGeographiesVariance?: number;
  numberOfUniqueVendorsVariance?: number;
  revenueVariance: number;
  totalCostAsPercentageOfRevenueVariance?: number;
  totalCostVariance?: number;
}

export enum ISpendByType {
  VENDORS = 'Vendor',
  SERVICE_TOWER = 'ServiceTower',
  GEOGRAPHY = 'Geography',
}

export enum IDisplayByType {
  COST = 'Spend',
  PERCENTAGE_OF_REVENUE = 'Percentage of revenue',
  HEADCOUNT = 'Headcount',
}
export interface ISelectOption {
  value: string;
  label: string;
  icon?: string;
  disabled?: boolean;
}

export interface ICompareYearTableRow {
  year?: IRange;
  spend?: string | number;
  revenue?: string | number;
  percentageRevenue?: string | number;
  headcount?: string | number;
  vendors?: string | number;
  legalEntities?: string | number;
  geographies?: string | number;
}

export type IRange = {
  startDate: string;
  endDate: string;
};

export type IQuarter = {
  currentPercentageOfRevenue?: number;
  currentRange: IPeriod;
  currentSpend: number;
  previousPercentageOfRevenue: number;
  previousRange: IPeriod;
  previousSpend: number;
  quarter: string;
  change: number;
  variancePercentageOfRevenue?: number;
};

export interface IWidget {
  comparisonDateRange: IRange;
  comparisonValue?: number;
  currentDateRange: IRange;
  currentValue?: number;
  netChange?: number;
  variance?: number;
}

export interface IComparisonWidgetData {
  title: string;
  currentPeriod: string;
  currentValue: string;
  previousPeriod: string;
  previousValue: string;
  netChange: number;
  variance: string;
  highlightedIndex?: number;
  format: ICurrencyFormat;
}

export interface IYearOnYearResponse {
  comparisonRange: IRange;
  range: IRange;
  quarterlyMovementWidget: IWidget;
  yoYComparisonWidget: IWidget;
  quarters: IQuarter[];
}

export type IYearOnYearChartSerie = {
  categories: string[];
  data: number[];
  color: string;
  legendLabel: string;
  tooltipDate: string[];
  quarters: IQuarter[];
};

export interface IYearOnYearChartProps {
  categories: Array<string>;
  series: IYearOnYearChartSerie[];
  format: ICurrencyFormat;
  chartRefObj?: Ref<{ chart: Chart; container: RefObject<HTMLDivElement> }>;
}

export type ICostByType = {
  change: number;
  comparisonValue: number;
  currentValue: number;
  dimValue: string;
};

export type IBreakdownSomething = {
  cost: number;
  dimValue: string;
  percentageOfTotalCost: number;
};

export interface IBreakdownPeriod extends IRange {
  contributionsBreakdown: IBreakdownSomething[];
  expenseTypesBreakdown: IBreakdownSomething[];
  spendTypesBreakdown: IBreakdownSomething[];
}

export type IBreakdownTable = {
  comparisonTotal: number;
  costsByContribution: ICostByType[];
  costsByExpenseType: ICostByType[];
  currentTotal: number;
  dimValue: string;
  totalChange: number;
};

export interface IBreakdownByResponse {
  businessUnits: IBreakdownTable[];
  capexQuarterlyMovementWidget: IWidget;
  capexYoYComparisonWidget: IWidget;
  comparisonPeriod: IBreakdownPeriod;
  currentPeriod: IBreakdownPeriod;
  geographies: IBreakdownTable[];
  opexQuarterlyMovementWidget: IWidget;
  opexYoYComparisonWidget: IWidget;
  personnelQuarterlyMovementWidget: IWidget;
  personnelYoYComparisonWidget: IWidget;
  thirdPartyQuarterlyMovementWidget: IWidget;
  thirdPartyYoYComparisonWidget: IWidget;
  g_AQuarterlyMovementWidget: IWidget;
  g_AYoYComparisonWidget: IWidget;
  nonG_AQuarterlyMovementWidget: IWidget;
  nonG_AYoYComparisonWidget: IWidget;
}

export interface ISpend {
  change: number;
  currentValue: number;
  dimValue: string;
  type: string;
  previousValue: number;
}

export interface ITopVendorsWidget {
  added: number;
  difference: number;
  removed: number;
}

export interface ITopVendorsResponse {
  comparisonRange: IRange;
  range: IRange;
  spends: ISpend[];
  topVendorsQuarterlyMovement: ITopVendorsWidget;
}

export type ITopItem = {
  change?: number | null;
  expenseType: string;
  previousTotal?: number | null;
  serviceTower: string;
  spendItem: string;
  total: number;
  vendor: string;
};

export interface ITopItemWidget {
  change: number;
  percentageOfTotal: number;
  previousPercentageOfTotal: number;
  variance: number;
}

export interface ITopItemsResponse {
  comparisonRange: IRange;
  range: IRange;
  items: ITopItem[];
  widget: ITopItemWidget;
}

export type ICurrencyConstants = {
  [key: string]: string;
};

export type ICellRendererParams = {
  rowIndex: number;
  value: number;
};

export type UNIT_TYPES = {
  Thousands: 'Thousands';
  Millions: 'Millions';
  Billions: 'Billions';
};

export type CURRENCY_TYPES = {
  USD: 'USD';
  GBP: 'GBP';
  EUR: 'EUR';
};

export type DECIMAL_TYPES = {
  None: 'None';
  '.0': '.0';
  '.00': '.00';
};

export type IDropdownOption = {
  label: string;
  value: string;
  disabled?: boolean;
};

export interface IFileData {
  actionDate: string;
  actorFullName: string;
  autoClassificationStatus: IAutoClassificationFileStatus;
  fileDataType: IFileDataType;
  fileName: string;
  fileState: IFileStatus;
  id: number;
}

export enum IFileDataType {
  THIRD_PARTY_SPEND = 'ThirdPartySpend',
  REVENUE = 'Revenue',
  PERSONNEL_SPEND = 'PersonnelSpend',
  APPLICATIONS = 'Applications',
  PRODUCTS = 'Products',
}

export enum IFileStatus {
  NOT_UPLOADED = 'NotUploaded',
  ERROR = 'Error',
  UPLOADED = 'Uploaded',
  UPLOAD_FAILED = 'UploadFailed',
  PUBLISHED = 'Published',
  INVALID = 'Invalid',
  IN_PROGRESS = 'UploadInProgress',
}

export enum IAutoClassificationFileStatus {
  NOT_STARTED = 'NotStarted',
  FAILED = 'Failed',
  CLASSIFIED = 'Classified',
  IN_PROGRESS = 'InProgress',
}

export type IUnit = keyof UNIT_TYPES;
export type IDecimal = keyof DECIMAL_TYPES;
export type ICurrency = keyof CURRENCY_TYPES;

export type IFileUnknownError = {
  CorrelationId: string;
  ErrorCode: any;
  ErrorMessage: string;
};

export type IParams = { [key: string]: string | string[] };

export interface IAnalysisProps {
  children: React.ReactNode;
  params: { [key: string]: string | string[] };
  setParams: (p: any) => void;
}

export enum INDUSTRY_PROVIDERS {
  GARTNER = 'Gartner',
  COMPUTER_ECONOMICS = 'Computer Economics',
}

export type IProviderIndustry = {
  l1Code: string;
  apIndustryL1Code: string;
  l1Category: string;
  provider: INDUSTRY_PROVIDERS.COMPUTER_ECONOMICS | INDUSTRY_PROVIDERS.GARTNER;
};

export type IApIndustry = {
  l1Code: string;
  l1Category: string;
};

export interface IIndustryAllocationProvider {
  provider: INDUSTRY_PROVIDERS.COMPUTER_ECONOMICS | INDUSTRY_PROVIDERS.GARTNER;
  code: string;
}

export interface IIndustryAllocation {
  allocationPercentage: number;
  apIndustryCode: string;
  providerIndustries: IIndustryAllocationProvider[];
}

export interface IIndustryAllocationLocal {
  allocationPercentage: number;
  apIndustryCode: string;
  gartnerIndustryCode: string;
  computerEconomicsIndustryCode: string;
}

export interface IIndustryBlend {
  benchmarkBreakDown?: any;
  id?: number;
  industryAllocations: IIndustryAllocation[];
  isActive: boolean;
  name: string;
  revenueUpliftPercentage?: number;
  year: number;
}

export interface IIndustryBlendLocal {
  benchmarkBreakDown?: IBenchmarkBreakdown[];
  id?: number;
  industryAllocations: IIndustryAllocationLocal[];
  isActive: boolean;
  name: string;
  revenueUpliftPercentage: number | string;
  year: number;
}

export interface IIndustryCalculation {
  providerName: string;
  value: string;
}

export type IBenchmarkCalculationType = 'ValueType' | 'JobRole';

export interface IBenchmarkCalculation {
  blendValue: string;
  industryCalculations: IIndustryCalculation[];
  type: IBenchmarkCalculationType;
  value: string;
  year: number;
}

export interface IBenchmarkBreakdown {
  providerName:
    | INDUSTRY_PROVIDERS.COMPUTER_ECONOMICS
    | INDUSTRY_PROVIDERS.GARTNER;
  benchmarkName: string;
  benchmarkCalculations: IBenchmarkCalculation[];
}

export type IProviderIndustryOption = {
  name: string;
  value: string;
  apIndustryL1Code: string;
};

export type IApIndustryOption = {
  name: string;
  value: string;
};

export type IAvailableProviderIndustry = {
  code: string;
  year: number;
};

export interface IDataAvailabilityResponse {
  requestedYear: number;
  availableProviderIndustries: IAvailableProviderIndustry[];
}

export interface IExportDataResponse {
  endDate: string;
  rowsInTotal: number;
  startDate: string;
}

export interface IHeadcountSpend {
  clientRole: string;
  cost: number;
  count: number;
  permanentOrContract: string;
}

export type ITopJobRole = {
  apSpendCategoryL1: string;
  apSpendCategoryL2: string;
  businessUnit: string;
  clientRole: string;
  contractorCount: number;
  cost: number;
  count: number;
  expenseType: string;
  location: string;
  permanentCount: number;
  permanentOrContract?: boolean;
  previousCost?: number;
  previousCount?: number;
};

export interface ITopJobRolesResponse {
  comparisonRange: IRange;
  previousTotalCost: number;
  previousTotalCount: number;
  range: IRange;
  table: ITopJobRole[];
  totalCost: number;
  totalCount: number;
}

export type ITableRow = {
  [x: string]: string | number | undefined | null;
};

export interface IApplicationSpendItem {
  applicationName: string;
  applicationType: string;
  businessOwner: string;
  contribution: string;
  groupName: string;
  spend: number;
  headcount: number;
  fte: number;
}

export interface IApplicationChartResponse {
  applicationsSpend: IApplicationSpendItem[];
}

export interface IStoredQueryItem {
  key: string;
  value: string;
}
